import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import apiConfig from '../../../constants/apiConfig';
import axios from 'axios';

// Discriminated union for files and folders
export type NotebookFolder = {
  id: UUID;
  type: 'folder';
  folder_name: string;
  color: string | null;
  file_name: null;
  size: null;
  mimetype: null;
  fileUrl: null;
  notefolderId: string | null;
  workspaceId: string;
  /**
   * Roster position this folder is tagged to, by seat name. Files inside it
   * show up on that position's tasks. Null on untagged folders.
   */
  position_name?: string | null;
  /** Roster template the tagged position name was picked from. */
  position_template_slug?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type NotebookFile = {
  id: UUID;
  type: 'file';
  folder_name: null;
  color: null;
  file_name: string;
  size: string;
  mimetype: string;
  fileUrl: string;
  notefolderId: string | null;
  workspaceId: string;
  rag_ready: boolean; // RAG processing status
  rag_processed_at?: string | null; // RAG processing timestamp
  createdAt: string;
  updatedAt: string;
};

export type FileOrFolder = NotebookFolder | NotebookFile;

export type NetworkResponse<T> = {
  response: T;
};

export type NotebookFolderListAllResponse = {
  dataset: FileOrFolder[];
  status: {
    msg: string;
    action_status: boolean;
  };
};

// Scope of the workspace a file/folder belongs to. 'organization' is used by the org-level
// file library (ownerId = group_guid); passing it lets the backend enforce org file permissions.
export type FileScopeType = 'incident' | 'exercise' | 'organization';

export const filesOrFoldersKeys = {
  all: ['filesOrFolders'],
  allFiles: (ownerId?: string) => ['filesOrFolders', ownerId],
};

export function useGetAllFiles(ownerId?: string, scopeType?: FileScopeType) {
  return useQuery<NotebookFolderListAllResponse, unknown>({
    queryKey: filesOrFoldersKeys.allFiles(ownerId),
    queryFn: async () => {
      // GET /dice/files/folders?recursive=true returns the flat
      // file+folder tree as a JSON array. Wrap it into the legacy
      // { dataset, status } envelope so existing consumers keep working.
      const response = await axios.get<FileOrFolder[]>(
        apiConfig.apiGateway.foldersBase,
        {
          params: {
            workspaceId: ownerId,
            recursive: true,
            ...(scopeType ? { scopeType } : {}),
          },
        }
      );
      return {
        dataset: response.data,
        status: { msg: 'OK', action_status: true },
      };
    },
    enabled: !!ownerId,
  });
}

// Utility for formatting dates
export function formatNotebookDate(
  dateString: string | null | undefined
): string {
  if (!dateString) return 'unknown';
  const d = dayjs(dateString);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : 'unknown';
}
